import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { AppProvider, useApp } from './context/AppContext';
import { I18nProvider, useI18n } from './context/I18nContext';
import { WhatsAppProvider } from './context/WhatsAppContext';
import { VoiceCommandProvider } from './context/VoiceCommandContext';
import SetupScreen from './components/SetupScreen';
import MainApp from './components/MainApp';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import PendingScreen from './components/PendingScreen';
import InviteModal from './components/InviteModal';
import HelpModal from './components/HelpModal';
import CharterModal from './components/CharterModal';
import ContactModal from './components/ContactModal';
import ThemeSelector from './components/ThemeSelector';
import ContactManager from './components/ContactManager';
import CollabPanel from './components/CollabPanel';
import { applyTheme, applyChatTransparency } from './themes';
import useFullscreen from './hooks/useFullscreen';
import Footer from './components/Footer';
import ScreenReaderAnnouncer from './components/ScreenReaderAnnouncer';
import TrialCountdown from './components/TrialCountdown';

const HistoryScreen = lazy(() => import('./components/HistoryScreen'));
const AdminMobileScreen = lazy(() => import('./components/AdminMobileScreen'));
const OnboardingWizard = lazy(() => import('./components/OnboardingWizard'));
// const FaceScreen = lazy(() => import('./components/FaceScreen')); // disabled for this phase
const PricingPlans = lazy(() => import('./components/PricingPlans'));
import SubscriptionBanner from './components/SubscriptionBanner';
// const VoiceCommandBar = lazy(() => import('./components/VoiceCommandBar')); // Voice features disabled
const LogoUploaderModal = lazy(() => import('./components/LogoUploaderModal'));

const LazyWrap = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

function AppContent() {
  useFullscreen();
  const {
    screen, setScreen, isAuthenticated, user, isAdmin, isSuperAdmin, login, logout,
    trialStatus, trialExpired, appConfig, validateTour,
    checkSetup, showInvite, showHelp, showCharter, showContact, setShowContact, showThemeSelector, showContactManager, collabOpen, showPricing, setShowPricing,
    sendQuestion, addAgent, removeAgent, messages, stopSpeaking, // speak disabled,
    agents, setAgents
  } = useApp();
  const [showRegister, setShowRegister] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const mainRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('cogency_theme') || 'default';
    applyTheme(savedTheme);
    const savedTransparency = localStorage.getItem('cogency_chat_transparent');
    applyChatTransparency(savedTransparency === 'true');
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.role !== 'pending') checkSetup();
  }, [checkSetup, isAuthenticated, user]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    // Admins bypass the onboarding wizard
    const isAdminUser = user.role === 'admin';
    setHasCompletedOnboarding(isAdminUser || user.tourValidated === true);
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (user?.plan === 'free' && !sessionStorage.getItem('pricing_dismissed') && !isAdmin) {
      setShowPricing(true);
    }
  }, [user, setShowPricing, isAdmin]);

  // Back button handling (Android hardware back)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Push a dummy history state so back button triggers popstate instead of exiting
    history.pushState({ cogencyBack: true }, '', location.href);

    const handlePopState = () => {
      // Re-push immediately so next back also triggers popstate
      history.pushState({ cogencyBack: true }, '', location.href);

      if (!isAuthenticated) {
        if (showRegister) { setShowRegister(false); return; }
        CapApp.exitApp();
        return;
      }
      if (user?.role === 'pending') {
        CapApp.exitApp();
        return;
      }
      if (screen === 'admin-mobile' || screen === 'history' || screen === 'main') {
        setScreen('setup');
        return;
      }
      if (screen === 'setup') {
        CapApp.exitApp();
        return;
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated, user?.role, screen, showRegister, setScreen]);

  // Web back-button navigation: ensure back from main/history/admin returns to setup
  const prevScreenForBackRef = useRef(screen);
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const prev = prevScreenForBackRef.current;
    prevScreenForBackRef.current = screen;
    if (prev === 'setup' && (screen === 'main' || screen === 'history' || screen === 'admin-mobile')) {
      history.pushState({ cogencyScreen: screen }, '', location.href);
    }
  }, [screen]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const handlePopState = (e) => {
      const stateScreen = e.state?.cogencyScreen;
      if (stateScreen && stateScreen !== screen) {
        setScreen(stateScreen);
      } else if (!stateScreen && screen !== 'setup') {
        setScreen('setup');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [screen, setScreen]);

  // Focus management: move focus to main content on screen change
  useEffect(() => {
    const timer = setTimeout(() => {
      const heading = mainRef.current?.querySelector('h1[tabindex="-1"], h2[tabindex="-1"]');
      if (heading) heading.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [screen, isAuthenticated, user?.role, showRegister]);

  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (isNative && isAuthenticated && user?.role !== 'pending') {
      if (hasCompletedOnboarding) {
        setScreen('setup');
      }
    }
  }, [isNative, isAuthenticated, user?.role, setScreen, hasCompletedOnboarding]);

  // Hard guard: FaceScreen is disabled for this phase; never render it
  useEffect(() => {
    if (screen === 'face') {
      setScreen('setup');
    }
  }, [screen, setScreen]);

  // Guard admin-only screens
  useEffect(() => {
    if (screen === 'admin-mobile' && !isAdmin) {
      setScreen('setup');
    }
  }, [screen, isAdmin, setScreen]);

  const { t, lang } = useI18n();

  // Voice command actions
  const voiceActions = useRef({
    goToLogin: () => { setShowRegister(false); },
    goToRegister: () => { setShowRegister(true); },
    logout,
    submitForm: () => {
      const form = mainRef.current?.querySelector('form');
      if (form) {
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn && !submitBtn.disabled) submitBtn.click();
      }
    },
    askQuestion: (question) => {
      if (screen !== 'main') setScreen('main');
      setTimeout(() => {
        const input = mainRef.current?.querySelector('.input-box input, .input-box textarea');
        if (input) {
          input.value = question;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          const sendBtn = mainRef.current?.querySelector('.input-box button');
          if (sendBtn && !sendBtn.disabled) sendBtn.click();
        }
      }, 300);
    },
    readLastMessage: () => {
      if (messages.length > 0) {
        const last = messages[messages.length - 1];
        if (last?.data?.content) {
          // speak(last.data.content); // voice disabled
        }
      }
    },
    readAllMessages: () => {
      const text = messages.map(m => {
        if (m.type === 'agent') return `${m.data.agent} said: ${m.data.content}`;
        if (m.type === 'final') return `Final answer: ${m.data.content}`;
        if (m.type === 'user') return `You asked: ${m.data.content}`;
        return '';
      }).filter(Boolean).join('. ');
      // if (text) speak(text); // voice disabled
    },
    stopSpeaking,
    addAgent,
    removeAgent,
    onCustomCommand: (cmd, raw, speakFn) => { // voice disabled
      // Context-specific commands handled here
    }
  });

  // Keep voiceActions ref up to date
  useEffect(() => {
    voiceActions.current = {
      ...voiceActions.current,
      goToLogin: () => { setShowRegister(false); },
      goToRegister: () => { setShowRegister(true); },
      logout,
      addAgent,
      removeAgent,
      stopSpeaking,
      // speak, // voice disabled
    };
  }, [logout, addAgent, removeAgent, stopSpeaking /* speak disabled */]);

  let content = null;
  if (!isAuthenticated) {
    if (showRegister) {
      content = <RegisterScreen onBackToLogin={() => setShowRegister(false)} />;
    } else {
      content = <LoginScreen onLogin={login} onShowRegister={() => setShowRegister(true)} />;
    }
  } else if (user?.role === 'pending') {
    content = <PendingScreen onLogout={logout} />;
  } else if (!hasCompletedOnboarding) {
    content = (
      <LazyWrap>
        <OnboardingWizard onComplete={() => { validateTour(); setHasCompletedOnboarding(true); }} />
      </LazyWrap>
    );
  } else {
    content = (
      <>
        {screen === 'setup' && <SetupScreen isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />}
        {screen === 'main' && <MainApp isAdmin={isAdmin} />}
        {screen === 'history' && <LazyWrap><HistoryScreen isAdmin={isAdmin} /></LazyWrap>}
        {screen === 'admin-mobile' && isSuperAdmin && <LazyWrap><AdminMobileScreen onBack={() => setScreen('setup')} /></LazyWrap>}
        {screen === 'contact-messages' && isSuperAdmin && <LazyWrap><ContactMessagesPanel /></LazyWrap>}
        {!isNative && showInvite && <InviteModal />}
        {!isNative && collabOpen && <CollabPanel />}
        {!isNative && showPricing && (
          <LazyWrap>
            <PricingPlans
              currentPlan={user?.plan || 'free'}
              onClose={() => { setShowPricing(false); sessionStorage.setItem('pricing_dismissed', '1'); }}
            />
          </LazyWrap>
        )}
        {!isNative && screen !== 'main' && <Footer />}
      </>
    );
  }

  const showAdminBanner = appConfig?.trialBanner?.enabled && appConfig?.trialBanner?.message && (
    (trialExpired && appConfig.trialBanner.showWhenExpired) ||
    (!trialExpired && trialStatus?.trialUser && appConfig.trialBanner.showWhenActive)
  );
  const bannerClass = [trialStatus && 'has-trial-banner', showAdminBanner && 'has-admin-banner'].filter(Boolean).join(' ');

  return (
    <VoiceCommandProvider
      lang={lang}
      t={t}
      navigate={setScreen}
      actions={voiceActions.current}
    >
      <TrialCountdown />
      <SubscriptionBanner onRenew={() => { sessionStorage.removeItem('pricing_dismissed'); setShowPricing(true); }} />
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <main id="main-content" ref={mainRef} className={bannerClass}>
        {content}
      </main>
      {showHelp && <HelpModal />}
      {showCharter && <CharterModal />}
      {showContact && <ContactModal onClose={() => setShowContact(false)} />}
      {showThemeSelector && <ThemeSelector />}
      {showContactManager && <ContactManager />}
      <ScreenReaderAnnouncer />
      {/* VoiceCommandBar disabled */}
      <LazyWrap><LogoUploaderModal /></LazyWrap>
    </VoiceCommandProvider>
  );
}

function App() {
  return (
    <I18nProvider>
      <AppProvider>
        <WhatsAppProvider>
          <AppContent />
        </WhatsAppProvider>
      </AppProvider>
    </I18nProvider>
  );
}

export default App;
